// src/lib/constants.ts

export const REQUIRED_SCRIPT_VERSION = "1.2.14";
export const BETA_SCRIPT_VERSION = "1.2.14";

// Sheet tab names - source of truth
export const SHEET_TABS = {
  ACCOUNT: "account",
  DAILY: "daily",
  HOURLY: "hourly",
  HOURLY_YEST: "hourly_yest",
  THIRTY_DAYS: "thirty_days",
  P_THIRTY_DAYS: "p_thirty_days",
  SEVEN_DAYS: "seven_days",
  P_SEVEN_DAYS: "p_seven_days",
  SETTINGS: "settings",
  PMAX_PERF: "pmax_perf",
  PMAX_NETWORK: "pmax_network",
  DG_NETWORK_PERF: "dg_network_perf",
  DG_ADS: "dg_ads",
  DG_ASSETS: "dg_assets",
  PRODUCTS: "products",
  PLACEMENTS: "placements",
  H_CAMP_30D: "h_camp_30d",
  H_CAMP_7D: "h_camp_7d",
  H_DOW_30D: "h_dow_30d",
  H_DOW_7D: "h_dow_7d",
  BIDDING: "bidding",
  QUALITY: "quality",
  KEYWORD80: "keyword80",
  SEARCH80: "search80",
  CAMP_CONV: "camp_conv",
  CONV_ACTION: "conv_action",
  KEYWORD80_PREV: "keyword80_prev",
  CAMPAIGN_IDS_SPEND: "campaign_ids_with_spend",
  CHANGES: "changes",
  ACCT_OVERVIEW: "acct_overview",
  CONV_TRENDS: "conv_trends",
  ST_INSIGHTS: "st_insights",
  KW_CPC: "kw_cpc",
  ASSETS: "assets",
  PMAX_ASSETS: "pmax_assets",
  AD_ASSETS: "ad_assets",
  RSA_ASSETS: "rsa_assets",
  CAMP_ASSETS: "camp_assets",
  AG_ASSETS: "ag_assets",
  GEO_PERF: "geo_perf",
  LPAGE_PERF: "lpage_perf",
  CAMPAIGN_BUDGETS: "campaign_budgets",
  WEEKLY_PERFORMANCE: "weekly_performance",
  SMART_BIDDING_CHANGES: "smart_bidding_changes",
  ON_PERFORMANCE: "on_performance",
  ON_SETTINGS: "on_settings",
  ON_DISAPPROVED: "on_disapproved",
  ON_EMPTY_AD_GROUPS: "on_empty_ad_groups",
  // Intent Mapper tabs
  IM_META: "im_meta",
  IM_CAMPAIGNS: "im_campaigns",
  IM_KEYWORDS: "im_keywords",
  IM_SEARCH_TERMS: "im_search_terms",
  IM_PRODUCTS: "im_products",
  IM_ASSET_GROUPS: "im_asset_groups",
  IM_PMAX_CATEGORIES: "im_pmax_categories",
  IM_PMAX_SEARCH_TERMS: "im_pmax_search_terms",
  IM_ADS: "im_ads",
  IM_AUDIENCES: "im_audiences",
} as const;

export type SheetTab = keyof typeof SHEET_TABS;
export type SheetTabValue = (typeof SHEET_TABS)[keyof typeof SHEET_TABS];

// Define ColumnDefinition interface (updated)
export interface ColumnDefinition {
  dataKey: string; // Changed from key
  header: string; // Changed from label
  dataType: "string" | "number" | "boolean" | "date"; // Changed from type
  metricKey?: string; // Added optional metricKey
  // Add other potential properties like formatters, visibility, etc., if needed elsewhere
}

// Define the new centralized data source definition
export interface DataSourceDefinition {
  key: SheetTabValue;
  title: string;
  description: string;
  defaultSort?: {
    column: string;
    direction: "asc" | "desc";
  };
  columns?: ColumnDefinition[];
}

// Helper function to generate title from key
function keyToTitle(key: string): string {
  if (!key) return "Untitled";
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const aiModelDataSourcesFromConfig: Record<
  string,
  {
    label: string;
    description: string;
    defaultSort?: { column: string; direction: "asc" | "desc" };
    type?: string;
    columns?: ColumnDefinition[];
  }
> = {
  version: {
    label: "Script Version Info",
    description:
      "Version information for the Google Ads script and account data.",
  },
  account: {
    label: "Account Details",
    description: "Basic account details and script version.",
  },
  daily: {
    label: "Daily Performance",
    description: "Daily campaign metrics including landing page performance",
    defaultSort: { column: "impr", direction: "desc" },
  },
  hourly: {
    label: "Hourly Performance",
    description: "Hour-by-hour performance data for today",
    defaultSort: { column: "impr", direction: "desc" },
  },
  hourly_yest: {
    label: "Hourly Performance (Yesterday)",
    description: "Hour-by-hour performance data for yesterday.",
    defaultSort: { column: "impr", direction: "desc" },
  },
  thirty_days: {
    label: "Last 30 Days Performance",
    description:
      "Overall campaign performance data aggregated over the last 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  p_thirty_days: {
    label: "Previous 30 Days Performance",
    description:
      "Overall campaign performance data aggregated over the 30 days prior to the last 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  seven_days: {
    label: "Last 7 Days Performance",
    description:
      "Overall campaign performance data aggregated over the last 7 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  p_seven_days: {
    label: "Previous 7 Days Performance",
    description:
      "Overall campaign performance data aggregated over the 7 days prior to the last 7 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  settings: {
    label: "Campaign Settings",
    description: "Detailed configuration settings for all campaigns.",
    defaultSort: { column: "campaign", direction: "asc" },
  },
  pmax_perf: {
    label: "Performance Max Performance",
    description:
      "Aggregated performance metrics specifically for Performance Max campaigns.",
    defaultSort: { column: "Total Cost", direction: "desc" },
  },
  products: {
    label: "Product Performance",
    description: "Product-level performance data including titles & metrics",
    defaultSort: { column: "impr", direction: "desc" },
  },
  placements: {
    label: "Placement Performance",
    description: "Website placement performance data (top 100 by cost)",
    defaultSort: { column: "impressions", direction: "desc" },
    columns: [
      { dataKey: "displayName", header: "Display Name", dataType: "string" },
      { dataKey: "placement", header: "Placement", dataType: "string" },
      { dataKey: "type", header: "Type", dataType: "string" },
      { dataKey: "targetUrl", header: "Target URL", dataType: "string" },
      { dataKey: "campaign", header: "Campaign", dataType: "string" },
      {
        dataKey: "impressions",
        header: "Impressions",
        dataType: "number",
        metricKey: "Impressions",
      },
    ],
  },
  h_camp_30d: {
    label: "Hourly by Campaign (Last 30d Avg)",
    description:
      "Average hourly performance data per campaign, aggregated over the last 30 days.",
    defaultSort: { column: "campaign", direction: "asc" },
  },
  h_camp_7d: {
    label: "Hourly by Campaign (Last 7d Avg)",
    description:
      "Average hourly performance data per campaign, aggregated over the last 7 days.",
    defaultSort: { column: "campaign", direction: "asc" },
  },
  h_dow_30d: {
    label: "Hourly by Day of Week (Last 30d Avg)",
    description:
      "Average hourly performance data per day of the week, aggregated over the last 30 days.",
    defaultSort: { column: "dow", direction: "asc" },
  },
  h_dow_7d: {
    label: "Hourly by Day of Week (Last 7d Avg)",
    description:
      "Average hourly performance data per day of the week, aggregated over the last 7 days.",
    defaultSort: { column: "dow", direction: "asc" },
  },
  bidding: {
    label: "Bidding Data",
    description: "Bidding strategy and target data",
    defaultSort: { column: "campaigns", direction: "desc" },
  },
  quality: {
    label: "Quality Score",
    description: "Quality score and component metrics",
    defaultSort: { column: "impr", direction: "desc" },
  },
  keyword80: {
    label: "Keywords (Last 30d)",
    description: "Performance data for keywords in the past 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  keyword80_prev: {
    label: "Keywords (Previous 30d)",
    description:
      "Performance data for keywords in the 30-day period prior to the most recent 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  search80: {
    label: "Search Terms (80/20)",
    description:
      "Performance data for search query terms, typically focusing on the 80/20 rule.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  camp_conv: {
    label: "Campaign Conversions",
    description: "Campaign-level conversion action data",
    defaultSort: { column: "conv", direction: "desc" },
  },

  // New Tabs added may 2025
  campaign_ids_with_spend: {
    label: "Campaign IDs with Spend",
    description:
      "List of campaign IDs that have incurred cost in the last 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  changes: {
    label: "Change History",
    description: "Account change events from the last 7 days.",
    defaultSort: { column: "date", direction: "desc" },
  },
  acct_overview: {
    label: "Account Overview (Daily)",
    description:
      "Daily aggregated performance metrics for the entire account over the last 90 days.",
    defaultSort: { column: "date", direction: "desc" },
  },
  conv_trends: {
    label: "Conversion Trends (Daily)",
    description:
      "Daily conversion action performance, by campaign, for the last 90 days.",
    defaultSort: { column: "date", direction: "desc" },
  },
  st_insights: {
    label: "Search Term Insights",
    description:
      "Machine-readable insights derived from search term trend analysis.",
  },
  kw_cpc: {
    label: "Keyword CPC Monitor (Last 7d)",
    description: "Keyword performance for the last 7 days, focusing on CPC.",
    defaultSort: { column: "avgCpc", direction: "desc" },
  },
  assets: {
    label: "Asset Details",
    description:
      "Details for various assets like text, image, and video assets.",
    defaultSort: { column: "assetType", direction: "asc" },
  },
  pmax_assets: {
    label: "PMax Asset Performance",
    description:
      "Performance data for assets within Performance Max campaigns, linked to asset groups.",
    defaultSort: { column: "impressions", direction: "desc" },
  },
  ad_assets: {
    label: "Ad-Level Asset Performance (Last 30d)",
    description:
      "Performance of assets at the ad level (e.g., RSA components) over the last 30 days.",
    defaultSort: { column: "impr", direction: "desc" },
  },
  camp_assets: {
    label: "Campaign-Level Asset Performance (Last 30d)",
    description:
      "Performance of assets directly linked at the campaign level over the last 30 days.",
    defaultSort: { column: "impr", direction: "desc" },
  },
  ag_assets: {
    label: "AdGroup Ad Performance (Last 30d)",
    description:
      "Performance data at the ad group ad level (e.g. individual ads) over the last 30 days.",
    defaultSort: { column: "impr", direction: "desc" },
  },
  geo_perf: {
    label: "Geographic Performance (Last 30d)",
    description:
      "Performance data by geographic locations for the last 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  lpage_perf: {
    label: "Landing Page Performance (Last 30d)",
    description: "Performance data for landing pages over the last 30 days.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  campaign_budgets: {
    label: "Campaign Budgets",
    description:
      "Campaign budget information including daily budget amounts, delivery methods, and shared budget status.",
    defaultSort: { column: "campaignName", direction: "asc" },
  },
  weekly_performance: {
    label: "Weekly Performance (Last 8 Weeks)",
    description:
      "Campaign performance data aggregated by week for the last 8 weeks, useful for Smart Bidding analysis.",
    defaultSort: { column: "week", direction: "desc" },
  },
  smart_bidding_changes: {
    label: "Smart Bidding Change History",
    description:
      "Change history for bidding strategies and budget modifications in the last 28 days.",
    defaultSort: { column: "changeDateTime", direction: "desc" },
  },

  // Custom/virtual data sources not directly from SHEET_TABS enum keys, but used for pre-processed data in prompts
  campaignProfitAnalysisV1: {
    type: "custom",
    label: "Campaign Profit Analysis (Processed)",
    description: "Profitability analysis for campaigns.",
  },
  campaignSettingsAuditDataV1: {
    type: "custom",
    label: "Campaign Settings Audit Data (Joined)",
    description: "Data about campaign settings and their audit history.",
  },
  smartBiddingAnalysisV1: {
    type: "custom",
    label: "Smart Bidding Analysis (Combined)",
    description:
      "Comprehensive campaign analysis combining performance, settings, and budget data for Smart Bidding readiness assessment.",
  },
  smartBiddingChanges: {
    type: "custom",
    label: "Smart Bidding Change History",
    description:
      "Change history for bidding strategies and budget modifications in the last 28 days.",
  },
  campaignScalingAnalysisV1: {
    type: "custom",
    label: "Campaign Scaling Analysis (Combined)",
    description:
      "Comprehensive campaign analysis for budget scaling readiness, including weekly performance trends, budget change impact, and impression share constraints.",
  },
  adCopyAnalysisV1: {
    type: "custom",
    label: "Ad Copy Analysis (Combined)",
    description:
      "Analysis of RSA ad copy performance, identifying underperforming ads with CTR below 7% and providing headline/description details for optimization.",
  },
  landingPageAnalysisV1: {
    type: "custom",
    label: "Landing Page Analysis (Combined)",
    description:
      "Analysis of landing page performance, identifying pages with conversion rates below threshold (3% ecomm, 5% leadgen) for optimization.",
  },

  // Intent Mapper data sources
  im_meta: {
    label: "Intent Mapper Meta",
    description: "Account metadata for Intent Mapper visualization.",
  },
  im_campaigns: {
    label: "Intent Mapper Campaigns",
    description: "Campaign data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_keywords: {
    label: "Intent Mapper Keywords",
    description: "Keyword data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_search_terms: {
    label: "Intent Mapper Search Terms",
    description: "Search term data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_products: {
    label: "Intent Mapper Products",
    description: "Product data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_asset_groups: {
    label: "Intent Mapper Asset Groups",
    description: "Asset group data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_ads: {
    label: "Intent Mapper Ads",
    description: "Ad data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_audiences: {
    label: "Intent Mapper Audiences",
    description: "Audience data for Intent Mapper visualization.",
    defaultSort: { column: "cost", direction: "desc" },
  },
  im_pmax_categories: {
    label: "PMax Search Categories",
    description: "Google's semantic groupings of PMax search queries. No cost data available.",
    defaultSort: { column: "impressions", direction: "desc" },
  },
  im_pmax_search_terms: {
    label: "PMax Search Terms (90%)",
    description: "Top PMax search terms by cost, covering 90% of total spend. Full metrics including cost and value.",
    defaultSort: { column: "cost", direction: "desc" },
  },
};

// Create the new comprehensive APP_DATA_SOURCES array
export const APP_DATA_SOURCES: DataSourceDefinition[] = [];
const addedKeys = new Set<string>();

// First, process all SHEET_TABS values
Object.values(SHEET_TABS).forEach((tabKeyValue: SheetTabValue) => {
  const existingConfig = aiModelDataSourcesFromConfig[tabKeyValue];
  let title = "";
  const sheetTabEnumKey = (Object.keys(SHEET_TABS) as SheetTab[]).find(
    (k) => SHEET_TABS[k] === tabKeyValue,
  );

  if (existingConfig) {
    title = existingConfig.label;
  } else if (sheetTabEnumKey) {
    title = keyToTitle(sheetTabEnumKey);
  } else {
    title = keyToTitle(tabKeyValue);
  }

  APP_DATA_SOURCES.push({
    key: tabKeyValue,
    title: title,
    description: existingConfig?.description || `Data for ${title}.`,
    defaultSort: existingConfig?.defaultSort,
    columns: existingConfig?.columns, // Ensure columns are passed through
  });
  addedKeys.add(tabKeyValue);
});

// Next, add any keys from aiModelDataSourcesFromConfig that weren't in SHEET_TABS
Object.keys(aiModelDataSourcesFromConfig).forEach((configKey) => {
  if (!addedKeys.has(configKey)) {
    const config = aiModelDataSourcesFromConfig[configKey];
    APP_DATA_SOURCES.push({
      key: configKey as SheetTabValue,
      title: config.label,
      description: config.description,
      defaultSort: config.defaultSort,
      columns: config.columns, // Ensure columns are passed through here too
    });
  }
});

// Sort the final array by title
APP_DATA_SOURCES.sort((a, b) => a.title.localeCompare(b.title));

// Get App URL from localStorage with validation
export const getWebAppUrl = (): string | null => {
  const url = localStorage.getItem(STORAGE_KEYS.WEB_APP_URL);
  if (!url) return null;

  // Validate URL format
  if (!url.includes("script.google.com/macros/s/")) {
    console.warn("Invalid Web App URL format found in storage. Clearing...");
    localStorage.removeItem(STORAGE_KEYS.WEB_APP_URL);
    return null;
  }

  // Check for invalid URLs
  if (url.includes("googleusercontent")) {
    console.warn("Invalid googleusercontent URL found in storage. Clearing...");
    localStorage.removeItem(STORAGE_KEYS.WEB_APP_URL);
    return null;
  }

  return url;
};

export const STORAGE_KEYS = {
  CAMPAIGN_DATA: "campaignData",
  ROW_LIMIT: "campaign_row_limit",
  WEB_APP_URL: "webAppUrl",
  SETUP_STATE: "setupState",
  SETUP_DATA: "setupData",
  USE_SAMPLE_DATA: "useSampleData",
  USER_CAMPAIGN_DATA: "userCampaignData",
  IS_CONFIGURED: "isConfigured",
  GAME_LEVEL: "currentGameLevel",
  CURRENT_COGS: "currentCogs",
  CURRENT_BREAK_EVEN: "currentBreakEven",
} as const;

// Cache duration in milliseconds
export const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours - increased from 1 hour for better data persistence

export const CURRENCIES = [
  { symbol: "$", label: "Dollar" },
  { symbol: "€", label: "Euro" },
  { symbol: "£", label: "Pound" },
  { symbol: "¥", label: "Yen" },
  { symbol: "zł", label: "Zloty" },
  { symbol: "R", label: "Rand" },
  { symbol: "₹", label: "Rupee" },
  { symbol: "₽", label: "Ruble" },
  { symbol: "₩", label: "Won" },
  { symbol: "₣", label: "Franc" },
  { symbol: "₫", label: "Dong" },
  { symbol: "₱", label: "Peso" },
  { symbol: "R$", label: "Real" },
  { symbol: "kr", label: "Krona/Krone" },
  { symbol: "₺", label: "Turkish Lira" },
  { symbol: "₴", label: "Hryvnia" },
  { symbol: "RON", label: "Romanian Leu" },
  { symbol: "฿", label: "Baht" },
  { symbol: "Ft", label: "Forint" },
] as const;

export const METRIC_FORMATS = {
  CURRENCY: (v: number) => `${Math.round(v).toLocaleString()}`,
  CURRENCY_2_DEC: (v: number) => `${v.toFixed(2)}`,
  NUMBER: (v: number) => Math.round(v).toLocaleString(),
  PERCENT: (v: number) => `${Math.round(v)}%`,
  PERCENT_1_DEC: (v: number) => `${v.toFixed(1)}%`,
  ROAS: (v: number) => `${v.toFixed(1)}x`,
} as const;

export const PMAX_METRICS = [
  {
    key: "cost",
    label: "Cost",
    description: "Total spend",
    format: (v: number) => `${Math.round(v).toLocaleString()}`,
  },
  {
    key: "cost_share",
    label: "Cost %",
    description: "Share of total cost",
    format: (v: number) => `${v.toFixed(1)}%`,
  },
  {
    key: "conversions",
    label: "Conv",
    description: "Total conversions",
    format: (v: number) => Math.round(v).toLocaleString(),
  },
  {
    key: "conv_share",
    label: "Conv %",
    description: "Share of total conversions",
    format: (v: number) => `${v.toFixed(1)}%`,
  },
  {
    key: "value",
    label: "Value",
    description: "Total conversion value",
    format: (v: number) => `${Math.round(v).toLocaleString()}`,
  },
  {
    key: "value_share",
    label: "Value %",
    description: "Share of total value",
    format: (v: number) => `${v.toFixed(1)}%`,
  },
] as const;

export const PMAX_CHANNELS = [
  "Shopping",
  "Display",
  "Video",
  "Search",
] as const;

// 8-channel breakdown (v1.2.6+) - uses segments.ad_network_type
export const PMAX_CHANNELS_8 = [
  "Shopping",
  "Search",
  "Display",
  "YouTube",
  "Gmail",
  "Discover",
  "Maps",
  "Partners",
] as const;

// Colors for 8-channel PMax breakdown
export const PMAX_CHANNEL_COLORS_8: Record<string, string> = {
  Shopping: "#115e59",   // Teal
  Search: "#4285F4",     // Google Blue
  Display: "#9CA3AF",    // Gray
  YouTube: "#FF0000",    // YouTube Red
  Gmail: "#EA4335",      // Gmail Red
  Discover: "#34A853",   // Google Green
  Maps: "#FBBC05",       // Google Yellow
  Partners: "#6B7280",   // Neutral Gray
};

// Metrics for 8-channel PMax (includes Value)
export const PMAX_METRICS_8 = [
  {
    key: "cost",
    label: "Cost",
    description: "Total spend",
    format: (v: number) => `${Math.round(v).toLocaleString()}`,
  },
  {
    key: "cost_share",
    label: "Cost %",
    description: "Share of total cost",
    format: (v: number) => `${v.toFixed(1)}%`,
  },
  {
    key: "conversions",
    label: "Conv",
    description: "Total conversions",
    format: (v: number) => Math.round(v).toLocaleString(),
  },
  {
    key: "conv_share",
    label: "Conv %",
    description: "Share of total conversions",
    format: (v: number) => `${v.toFixed(1)}%`,
  },
  {
    key: "value",
    label: "Value",
    description: "Total conversion value",
    format: (v: number) => `${Math.round(v).toLocaleString()}`,
  },
  {
    key: "value_share",
    label: "Value %",
    description: "Share of total value",
    format: (v: number) => `${v.toFixed(1)}%`,
  },
] as const;

export type PMaxChannel = (typeof PMAX_CHANNELS)[number];
export type PMaxChannel8 = (typeof PMAX_CHANNELS_8)[number];
export type PMaxMetricKey = (typeof PMAX_METRICS)[number]["key"];
export type PMaxMetricKey8 = (typeof PMAX_METRICS_8)[number]["key"];
export type TabKey = SheetTabValue;
export type FetchStatus = "loading" | "success" | "error";

// Setup state management
interface SetupState {
  isComplete: boolean;
  sheetUrl?: string;
  webAppUrl?: string;
  completedAt?: string;
  currentStep?: number;
}

// Cache the configuration status
let configurationStatus: boolean | null = null;
let lastCheck = 0;
const CHECK_INTERVAL = 5000; // 5 seconds

export const isConfigured = () => {
  const now = Date.now();

  // Only check if we haven't checked before or if enough time has passed
  if (configurationStatus === null || now - lastCheck > CHECK_INTERVAL) {
    const setupData = localStorage.getItem(STORAGE_KEYS.SETUP_DATA);
    configurationStatus = !!setupData;
    lastCheck = now;
  }

  return configurationStatus;
};

// Add a function to reset the cache when needed
export const resetConfigurationStatus = () => {
  configurationStatus = null;
};

export const getSetupState = (): SetupState | null => {
  try {
    const state = localStorage.getItem(STORAGE_KEYS.SETUP_STATE);
    return state ? JSON.parse(state) : null;
  } catch (error) {
    console.error("Error parsing setup state:", error);
    return null;
  }
};

export const saveSetupState = (state: SetupState): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.SETUP_STATE, JSON.stringify(state));
  } catch (error) {
    console.error("Error saving setup state:", error);
  }
};

export const completeSetup = (
  sheetUrl: string,
  webAppUrl: string,
): SetupState => {
  // Validate URL format
  if (!webAppUrl.includes("script.google.com/macros/s/")) {
    throw new Error(
      "Invalid Web App URL format. Must be a Google Apps Script web app URL.",
    );
  }

  // Prevent googleusercontent URLs
  if (webAppUrl.includes("googleusercontent")) {
    throw new Error(
      "Invalid URL: Contains googleusercontent. Please use the proper Web App URL.",
    );
  }

  const state: SetupState = {
    isComplete: true,
    sheetUrl,
    webAppUrl,
    completedAt: new Date().toISOString(),
    currentStep: 4,
  };

  saveSetupState(state);
  localStorage.setItem(STORAGE_KEYS.WEB_APP_URL, webAppUrl);
  localStorage.setItem(STORAGE_KEYS.IS_CONFIGURED, "true");

  return state;
};

// Add to existing constants
export const MAX_FREE_ACCOUNTS = 5;
export const SAMPLE_ACCOUNT_ID = "111-111-1111";
export const SAMPLE_ACCOUNT_SCRIPT_VERSION = "0.9.10";
export const DATA_STRUCTURE_VERSION = 2;

interface VersionRequirement {
  minVersion: string;
  label?: string;
}

export const PAGE_VERSION_REQUIREMENTS = {
  pages: {
    keywords: {
      minVersion: "0.4.46",
    },
  } satisfies Record<string, VersionRequirement>,
  features: {
    "180-day-trends": {
      minVersion: "0.4.25",
      label: "180 Day History",
    },
  } satisfies Record<string, VersionRequirement>,
} as const;

export const SAMPLE_ACCOUNT = {
  id: SAMPLE_ACCOUNT_ID,
  dexieId: SAMPLE_ACCOUNT_ID,
  accountCID: "sample-cid",
  scriptVersion: SAMPLE_ACCOUNT_SCRIPT_VERSION,
  name: "Sample Account",
  businessMode: "ecomm",
  currency: "$",
  cogsAmount: 55,
  breakEvenCpa: 33,
  webAppUrl: "/sample-data",
  isSampleAccount: true,
};

// Add DEBUG constant
export const DEBUG = true;

// Generate app category URLs for exclusions
function generateAppCategories() {
  const categories: string[] = [];

  // First range: 60001-60062
  for (let i = 1; i <= 62; i++) {
    categories.push(`mobileappcategory::600${i.toString().padStart(2, "0")}`);
  }

  // Second range: 60501-60587
  for (let i = 1; i <= 87; i++) {
    categories.push(`mobileappcategory::605${i.toString().padStart(2, "0")}`);
  }

  return categories;
}

export const APP_CATEGORIES = generateAppCategories();

export type RecordLimit = 10 | 50 | 100 | 250 | 500 | 1000 | "all";

export const DEFAULT_COGS = 0.25; // 25%
export const DEFAULT_BREAK_EVEN_CPA = 50;

export const DEMO_MODE = false;
